import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Menu, LayoutDashboard, User, Users, Timer,
  ChevronDown, ChevronUp, X,
} from "lucide-react";
import { SearchBar } from "@/components/search/SearchBar";
import { Logo } from "./Logo";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/use-auth-compat";
import { useRole } from "@/contexts/RoleContext";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

interface HeaderProps {
  /**
   * Hide the "Dashboard" button for authenticated users. Used during the
   * student onboarding sequence (e.g. goal setup) so users complete the flow
   * instead of bailing out to their dashboard.
   */
  hideDashboard?: boolean;
}

const CATEGORY_LINKS: { label: string; to: string; icon?: React.ElementType }[] = [
  { label: "1-on-1 Tutors",   to: "/private-tutoring-sessions",        icon: User },
  { label: "Group Classes",   to: "/online-group-classes",             icon: Users },
  { label: "Crash Courses",   to: "/exam-crash-courses",               icon: Timer },
  { label: "Class 10",        to: "/search/one-to-one?q=Class+10" },
  { label: "Class 12",        to: "/search/one-to-one?q=Class+12" },
  { label: "JEE",             to: "/search/one-to-one?q=JEE" },
  { label: "NEET",            to: "/search/one-to-one?q=NEET" },
  { label: "UPSC",            to: "/search/one-to-one?q=UPSC" },
  { label: "CAT",             to: "/search/one-to-one?q=CAT" },
  { label: "Spoken English",  to: "/search/one-to-one?q=Spoken+English" },
  { label: "Coding",          to: "/search/one-to-one?q=Coding" },
];

// ── Nav search bar ────────────────────────────────────────────────────────────
function NavSearchBar() {
  return (
    <div className="flex-1 mx-6 max-w-xl hidden lg:block">
      <SearchBar compact />
    </div>
  );
}

// ── Mega-menu ─────────────────────────────────────────────────────────────────
function MegaMenu({ onClose }: { onClose: () => void }) {
  return (
    <div className="absolute left-0 right-0 top-full bg-white shadow-xl border-t border-border z-40">
      <div className="container mx-auto px-4 lg:px-8 py-8 grid grid-cols-5 gap-8">

        {/* Col 1: Ways to learn */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Ways to learn</p>
          <div className="flex flex-col gap-3">
            <Link to="/private-tutoring-sessions" onClick={onClose} className="flex items-start gap-3 group">
              <div className="w-9 h-9 rounded-lg bg-[#EBF1FB] flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-4 h-4 text-[#2F6BD8]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground group-hover:text-[#2F6BD8] transition-colors">1-on-1 Tutors</p>
                <p className="text-xs text-muted-foreground">One teacher, lessons at your pace</p>
              </div>
            </Link>
            <Link to="/online-group-classes" onClick={onClose} className="flex items-start gap-3 group">
              <div className="w-9 h-9 rounded-lg bg-[#E6F5F3] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Users className="w-4 h-4 text-[#0E8A78]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground group-hover:text-[#0E8A78] transition-colors">Group Classes</p>
                <p className="text-xs text-muted-foreground">Batches with a fixed timetable</p>
              </div>
            </Link>
            <Link to="/exam-crash-courses" onClick={onClose} className="flex items-start gap-3 group">
              <div className="w-9 h-9 rounded-lg bg-[#FBECE9] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Timer className="w-4 h-4 text-[#D9472B]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground group-hover:text-[#D9472B] transition-colors">Crash Courses</p>
                <p className="text-xs text-muted-foreground">Short, intensive exam prep</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Col 2: School subjects */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">School subjects</p>
          <ul className="flex flex-col gap-2">
            {[
              { label: "Maths",           to: "/search/one-to-one?q=Maths" },
              { label: "Science",         to: "/search/one-to-one?q=Science" },
              { label: "English",         to: "/search/one-to-one?q=English" },
              { label: "Class 10 boards", to: "/search/one-to-one?q=Class+10" },
              { label: "Class 12 boards", to: "/search/one-to-one?q=Class+12" },
            ].map(({ label, to }) => (
              <li key={label}>
                <Link to={to} onClick={onClose} className="text-sm text-foreground hover:text-[hsl(237,52%,31%)] transition-colors">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3: Exam preparation */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Exam preparation</p>
          <ul className="flex flex-col gap-2">
            {["JEE", "NEET", "UPSC", "CAT", "GATE", "IELTS"].map((label) => (
              <li key={label}>
                <Link
                  to={`/search/one-to-one?q=${encodeURIComponent(label)}`}
                  onClick={onClose}
                  className="text-sm text-foreground hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 4: Languages and skills */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Languages and skills</p>
          <ul className="flex flex-col gap-2">
            {[
              { label: "Spoken English",  to: "/search/one-to-one?q=Spoken+English" },
              { label: "Other languages", to: "/search/one-to-one?q=Languages" },
              { label: "Coding",          to: "/search/one-to-one?q=Coding" },
              { label: "All subjects",    to: "/subjects" },
            ].map((item) => (
              <li key={item.label}>
                <Link
                  to={item.to}
                  onClick={onClose}
                  className="text-sm text-foreground hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 5: Teach promo */}
        <div className="bg-[#FFF1D6] rounded-2xl p-6 flex flex-col gap-3">
          <h3 className="text-base font-bold text-[hsl(237,52%,15%)]">Teach on BookMyCoaching</h3>
          <p className="text-sm text-[hsl(237,30%,35%)] leading-relaxed">
            Set your fees, choose your schedule and reply to student enquiries.
          </p>
          <Link
            to="/become-an-online-tutor"
            onClick={onClose}
            className="mt-auto inline-flex items-center justify-center text-sm font-semibold bg-[hsl(39,96%,48%)] hover:bg-[hsl(39,96%,42%)] text-[hsl(237,52%,15%)] px-4 py-2 rounded-full transition-colors"
          >
            Become a Tutor
          </Link>
        </div>

      </div>
    </div>
  );
}

// ── Mobile accordion section ──────────────────────────────────────────────────
function DrawerSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#DCDDEE]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-[#262A7A]"
      >
        {title}
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="px-5 pb-4 flex flex-col gap-3">{children}</div>}
    </div>
  );
}

export function Header({ hideDashboard = false }: HeaderProps = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { role } = useRole();
  const { user } = useCurrentUser();
  const effectiveRole = role ?? user?.defaultRole?.toLowerCase() ?? null;
  const hasNoRole = !role && !!user && !user.defaultRole && (user.roles?.length ?? 0) === 0;
  const dashboardHref = hasNoRole
    ? "/select-role"
    : effectiveRole === "teacher" ? "/teacher-dashboard" : "/student-dashboard";

  // Close mega-menu on outside click or Escape
  useEffect(() => {
    if (!megaMenuOpen) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMegaMenuOpen(false);
    }
    function handleClick(e: MouseEvent) {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target as Node)) {
        setMegaMenuOpen(false);
      }
    }
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [megaMenuOpen]);

  return (
    <>
    <header
      ref={megaMenuRef}
      className="fixed top-0 left-0 right-0 z-50 bg-background backdrop-blur-xl border-b border-border/50"
    >

      {/* ── Layer 1: Topbar (desktop only) ── */}
      <div className="hidden lg:block bg-[hsl(237,52%,20%)] text-[13px]">
        <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between h-8">
          <span className="flex items-center gap-1.5 text-[hsl(237,20%,75%)]">
            Teach on BookMyCoaching.{" "}
            <Link to="/become-an-online-tutor" className="font-semibold text-[hsl(39,96%,65%)] hover:underline">
              Become a Tutor →
            </Link>
          </span>
          <div className="flex items-center gap-5 text-[hsl(237,20%,70%)]">
            <Link to="/contact" className="hover:text-white transition-colors">Contact us</Link>
          </div>
        </div>
      </div>

      {/* ── Layer 2: Main nav bar ── */}
      <nav
        className="container mx-auto flex items-center justify-between py-3 px-4 lg:px-8"
        aria-label="Global"
      >
        <div className="flex items-center gap-3 flex-shrink-0">
          <Logo />
          {/* Explore button — desktop only */}
          <button
            type="button"
            onClick={() => setMegaMenuOpen((o) => !o)}
            className="hidden lg:flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            Explore
            {megaMenuOpen ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Search bar — desktop (hidden on home page where hero has its own search) */}
        {pathname !== "/" && <NavSearchBar />}

        <div className="hidden lg:flex items-center gap-4 flex-shrink-0" id="hd-actions">
          <Link
            to="/how-it-works"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("nav.howItWorks")}
          </Link>

          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              {!hideDashboard && (
                <Button
                  className="rounded-full px-6 gap-2"
                  size="sm"
                  onClick={() => navigate(dashboardHref)}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="rounded-full px-6"
                size="sm"
                onClick={() => navigate("/register")}
              >
                {t("nav.signUp")}
              </Button>
              <Button
                className="rounded-full px-6 gap-2"
                size="sm"
                onClick={() => navigate("/login")}
              >
                {t("nav.login")}
              </Button>
            </div>
          )}
        </div>

        <div className="flex lg:hidden">
          <button
            type="button"
            className="p-2.5 text-foreground cursor-pointer"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      {/* ── Layer 3: Category nav bar (desktop only) ── */}
      <div className="hidden lg:block border-t border-border/50">
        <nav
          aria-label="Course categories"
          className="container mx-auto px-4 lg:px-8 flex items-center gap-1 h-9 overflow-x-auto scrollbar-none"
        >
          {/* First 3: format links — active when on that page */}
          {CATEGORY_LINKS.filter(c => c.icon).map((cat) => {
            const isActive = pathname === cat.to || pathname.startsWith(cat.to + "/");
            return (
              <Link
                key={cat.label}
                to={cat.to}
                className={cn(
                  "whitespace-nowrap text-[13px] font-semibold px-3 py-1 rounded-full transition-colors flex-none flex items-center gap-1.5",
                  isActive
                    ? "bg-[#262A7A] text-white"
                    : "text-[#5A5E80] hover:text-[#262A7A] hover:bg-[#EEEFF9]"
                )}
              >
                {cat.icon && <cat.icon className="w-3.5 h-3.5" aria-hidden="true" />}
                {cat.label}
              </Link>
            );
          })}

          {/* Separator */}
          <span className="w-px h-[22px] bg-border flex-none mx-1.5" aria-hidden="true" />

          {/* Remaining: topic/exam links */}
          {CATEGORY_LINKS.filter(c => !c.icon).map((cat) => (
            <Link
              key={cat.label}
              to={cat.to}
              className="whitespace-nowrap text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 px-3 py-1 rounded-full transition-colors flex-none"
            >
              {cat.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* ── Mega-menu panel ── */}
      {megaMenuOpen && (
        <MegaMenu onClose={() => setMegaMenuOpen(false)} />
      )}

    </header>

      {/* ── Mobile drawer ── */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0" style={{ zIndex: 9999 }}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div
            className="absolute top-0 right-0 w-80 h-full bg-white shadow-2xl flex flex-col overflow-y-auto"
            style={{ zIndex: 10000 }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#DCDDEE] flex-shrink-0">
              <Logo />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Accordion sections */}
            <div className="flex-1 overflow-y-auto">
              <DrawerSection title="Ways to learn" defaultOpen>
                <Link
                  to="/private-tutoring-sessions"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  <User className="w-4 h-4 flex-shrink-0" />
                  1-on-1 Tutors
                </Link>
                <Link
                  to="/online-group-classes"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  <Users className="w-4 h-4 flex-shrink-0" />
                  Group Classes
                </Link>
                <Link
                  to="/exam-crash-courses"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  <Timer className="w-4 h-4 flex-shrink-0" />
                  Crash Courses
                </Link>
              </DrawerSection>

              <DrawerSection title="School subjects">
                {[
                  { label: "Maths",           to: "/search/one-to-one?q=Maths" },
                  { label: "Science",         to: "/search/one-to-one?q=Science" },
                  { label: "English",         to: "/search/one-to-one?q=English" },
                  { label: "Class 10 boards", to: "/search/one-to-one?q=Class+10" },
                  { label: "Class 12 boards", to: "/search/one-to-one?q=Class+12" },
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </DrawerSection>

              <DrawerSection title="Exam preparation">
                {[
                  { label: "JEE",  to: "/search/one-to-one?q=JEE" },
                  { label: "NEET", to: "/search/one-to-one?q=NEET" },
                  { label: "UPSC", to: "/search/one-to-one?q=UPSC" },
                  { label: "CAT",  to: "/search/one-to-one?q=CAT" },
                  { label: "GATE", to: "/search/one-to-one?q=GATE" },
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </DrawerSection>

              <DrawerSection title="For teachers">
                <Link
                  to="/become-an-online-tutor"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  Become a Tutor
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  Gold and Platinum plans
                </Link>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm text-[#262A7A] hover:text-[hsl(237,52%,31%)] transition-colors"
                >
                  Tutor login
                </Link>
              </DrawerSection>
            </div>

            {/* Bottom CTAs */}
            <div className="px-5 py-5 border-t border-[#DCDDEE] flex flex-col gap-3 flex-shrink-0">
              {isAuthenticated ? (
                !hideDashboard && (
                  <Button
                    className="w-full rounded-full gap-2"
                    onClick={() => { setMobileMenuOpen(false); navigate(dashboardHref); }}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Button>
                )
              ) : (
                <>
                  <Button
                    className="w-full rounded-full font-semibold"
                    onClick={() => { setMobileMenuOpen(false); navigate("/register"); }}
                  >
                    Sign up free
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full rounded-full font-semibold"
                    onClick={() => { setMobileMenuOpen(false); navigate("/login"); }}
                  >
                    Log in
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
